/**
 * CL-CLIENT-UX-001 — Client portal module applicability (pure).
 *
 * Future modules require BOTH:
 *   explicitAdminPermission AND projectModuleIsApplicable
 *
 * Does not invent a second stage enum — reuses PROJECT_JOURNEY_STAGE_DEFS keys.
 * Module permission flags are optional explicit booleans on the collaborator
 * permissions object (not defaulted / not persona-inferred).
 */

import {
  PROJECT_JOURNEY_STAGE_DEFS,
  normalizeProjectJourneyControl,
  normalizeProjectJourneyStageKey,
} from "./projectJourneyAdminModel";
import { PROJECT_TABS } from "./projectPortalTabsModel";
import { CLIENT_PORTAL_MODULE_PERMISSION_KEYS } from "./clientPortalModulePermissionKeys";

export { CLIENT_PORTAL_MODULE_PERMISSION_KEYS } from "./clientPortalModulePermissionKeys";

function safeStr(value) {
  return String(value ?? "").trim();
}

function stageIndex(stageKey) {
  const key = normalizeProjectJourneyStageKey(stageKey);
  const ix = PROJECT_JOURNEY_STAGE_DEFS.findIndex((d) => d.key === key);
  return ix < 0 ? 0 : ix;
}

export function readProjectJourneyStageKey(project = null) {
  const control = normalizeProjectJourneyControl(
    project?.journeyControl || project?.projectJourneyControl || project?.projectJourney || {}
  );
  return control.currentStageKey;
}

/**
 * Whether a journey stage has been reached (current stage index >= target).
 * Stages already passed remain applicable after the project advances.
 */
export function projectJourneyHasReachedStage(project, targetStageKey) {
  const current = readProjectJourneyStageKey(project);
  return stageIndex(current) >= stageIndex(targetStageKey);
}

function explicitProjectModuleFlag(project, ...paths) {
  for (const path of paths) {
    const parts = safeStr(path).split(".");
    let cur = project;
    for (const p of parts) {
      if (cur == null || typeof cur !== "object") {
        cur = undefined;
        break;
      }
      cur = cur[p];
    }
    if (cur === true) return true;
  }
  return false;
}

/**
 * Module applicability using existing stage + optional project activation flags.
 * Previously activated flags remain true after the journey advances.
 */
export function isClientPortalModuleApplicable(moduleKey, project = null) {
  const key = safeStr(moduleKey).toLowerCase();
  if (key === "messages") return true; // permission-only; no stage gate
  if (key === "schedule") {
    return (
      explicitProjectModuleFlag(
        project,
        "clientModules.schedule",
        "portalModules.schedule",
        "clientScheduleActivated",
        "schedulePublishedToClient"
      ) || true
    );
  }
  if (key === "construction") {
    return (
      explicitProjectModuleFlag(
        project,
        "clientModules.construction",
        "portalModules.construction",
        "clientConstructionActivated"
      ) || projectJourneyHasReachedStage(project, "construction")
    );
  }
  if (key === "financials") {
    return (
      explicitProjectModuleFlag(
        project,
        "clientModules.financials",
        "portalModules.financials",
        "clientFinancialsPublished",
        "financialsPublishedToClient",
        "budgetPublishedToClient"
      ) || projectJourneyHasReachedStage(project, "construction")
    );
  }
  if (key === "resources") {
    return (
      explicitProjectModuleFlag(
        project,
        "clientModules.resources",
        "portalModules.resources",
        "clientPermitsResourcesActivated",
        "permitsResourcesActivated"
      ) ||
      // Permit workflow becomes applicable once permit submission is underway or later.
      projectJourneyHasReachedStage(project, "permit_submission")
    );
  }
  if (key === "suppliers") {
    return (
      explicitProjectModuleFlag(
        project,
        "clientModules.suppliers",
        "portalModules.suppliers",
        "clientSuppliersPublished",
        "suppliersPublishedToClient"
      ) || projectJourneyHasReachedStage(project, "construction")
    );
  }
  return false;
}

export function hasExplicitClientPortalModulePermission(permissions = {}, moduleKey = "") {
  const permKey = CLIENT_PORTAL_MODULE_PERMISSION_KEYS[safeStr(moduleKey).toLowerCase()];
  if (!permKey) return false;
  return permissions?.[permKey] === true;
}

/**
 * @returns {boolean} visible when explicit permission is granted; suppliers override journey stage.
 */
export function isClientPortalModuleVisibleToClient(moduleKey, { permissions = {}, project = null } = {}) {
  if (!hasExplicitClientPortalModulePermission(permissions, moduleKey)) {
    return false;
  }
  const module = safeStr(moduleKey).toLowerCase();
  if (module === "suppliers" || module === "schedule") {
    return true;
  }
  return isClientPortalModuleApplicable(moduleKey, project);
}

export function resolveClientPortalFutureModuleVisibility({ permissions = {}, project = null } = {}) {
  return {
    schedule: isClientPortalModuleVisibleToClient("schedule", { permissions, project }),
    construction: isClientPortalModuleVisibleToClient("construction", { permissions, project }),
    financials: isClientPortalModuleVisibleToClient("financials", { permissions, project }),
    resources: isClientPortalModuleVisibleToClient("resources", { permissions, project }),
    messages: isClientPortalModuleVisibleToClient("messages", { permissions, project }),
    suppliers: isClientPortalModuleVisibleToClient("suppliers", { permissions, project }),
  };
}

export function clientPortalTabKeyForModule(moduleKey) {
  const key = safeStr(moduleKey).toLowerCase();
  if (key === "schedule") return PROJECT_TABS.SCHEDULE;
  if (key === "construction") return PROJECT_TABS.CONSTRUCTION;
  if (key === "financials") return PROJECT_TABS.FINANCIALS;
  if (key === "resources") return PROJECT_TABS.RESOURCES;
  if (key === "messages") return PROJECT_TABS.MESSAGES;
  if (key === "suppliers") return PROJECT_TABS.TRADES_SUPPLIERS;
  return "";
}
